import type { SeatInfo } from "@app/protocol";
import type { PillTone, SeatLook, TurnClock } from "../components/Pod";
import type { useT } from "../i18n/context";
import { FACTS } from "../variants/facts";
import { type AnyView, isChoosingPass, isPile } from "../variants/types";
import { wordsOf } from "../variants/words";

export type T = ReturnType<typeof useT>["t"];

/** What a seat's pill says during play (design 4-7). */
export function seatSub(
  view: AnyView,
  seat: number,
  connected: boolean,
  yourSeat: number | null,
  t: T,
): string {
  if (!connected) return t("table.seat.away");
  if (seat === yourSeat && view.turn === seat && view.phase === "playing")
    return t("table.turn.play");
  return wordsOf(view).seatSub(view, seat, yourSeat, t);
}

/** Away, a bot, or a seat the table is playing for its player (decision 173). */
function seatBadge(seat: SeatInfo): "away" | "bot" | "auto" | undefined {
  if (!seat.connected) return "away";
  if (seat.bot) return "bot";
  return seat.auto === true ? "auto" : undefined;
}

/** A seat's pod at the table: the line, white while it is their move, red when dropped; the clock. */
export function tableSeatLook(
  view: AnyView,
  seat: SeatInfo | null,
  index: number,
  yourSeat: number | null,
  clock: TurnClock | null,
  t: T,
): SeatLook {
  if (seat === null) return { status: t("lobby.empty"), tone: "dark" };
  // Hearts' pass: everyone still choosing is on the clock together (ADR 0046).
  const turn = view.turn === index || isChoosingPass(view, index);
  // Pişti: while a capture rests nobody is on turn, and the eye belongs on whoever took the pile.
  const took = isPile(view) && view.sweep?.seat === index;
  const tone: PillTone = !seat.connected ? "alert" : turn || took ? "light" : "dark";
  const held = FACTS[view.variant].handBacks ? (view.handCounts[index] ?? 0) : 0;
  return {
    status: seatSub(view, index, seat.connected, yourSeat, t),
    tone,
    badge: seatBadge(seat),
    clock: turn ? clock : null,
    hand: held,
  };
}
